import { useEffect, useState, useCallback, useRef } from "react";
import {
  Loader2, RefreshCw, Store, MapPin, Package, Tag, Warehouse, Percent, Users, CheckCircle2, Database, ArrowRight, FileText, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { ConnectionPanel } from "@/components/odoo/ConnectionPanel";

const ENTITIES = [
  { key: "customers", label: "Customer (Restricted POS)", icon: Store, cls: "green" },
  { key: "addresses", label: "Alamat Kirim", icon: MapPin, cls: "blue" },
  { key: "products", label: "Produk Saleable", icon: Package, cls: "orange" },
  { key: "pricelists", label: "Pricelist & Item", icon: Tag, cls: "green" },
  { key: "warehouses", label: "Warehouse", icon: Warehouse, cls: "blue" },
  { key: "taxes", label: "Pajak (Sales Tax)", icon: Percent, cls: "orange" },
  { key: "employees", label: "Karyawan (Employee)", icon: Users, cls: "green" },
  { key: "payment_terms", label: "AR Term (Payment Terms)", icon: CalendarClock, cls: "orange" },
  { key: "orders", label: "Sales Order (Import Odoo)", icon: FileText, cls: "blue" },
];

const ENTITY_LABEL = { customers: "Customer", addresses: "Alamat", products: "Produk", pricelists: "Pricelist", warehouses: "Warehouse", taxes: "Pajak", employees: "Karyawan", orders: "Sales Order", payment_terms: "AR Term" };
const ENV_LABEL = { production: "PRODUCTION", demo: "DEMO", off: "NONAKTIF" };

export default function OdooSync() {
  const { hasPerm } = useAuth();
  const [state, setState] = useState({ latest: {}, runs: [] });
  const [odooLive, setOdooLive] = useState(false);
  const [odooEnv, setOdooEnv] = useState("off");
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const canSync = hasPerm("odoo:sync");
  const running = job?.status === "running";

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/odoo/sync-runs"); setState(r.data.data); setOdooLive(!!r.data.meta?.odoo_configured); setOdooEnv(r.data.meta?.odoo_env || "off"); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); setReady(true); }
  }, []);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const startPoll = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/odoo/sync/job");
        const j = r.data.data;
        setJob(j);
        if (!j || j.status !== "running") {
          stopPoll(); setSyncing(null);
          if (j && j.status === "done") { toast.success("Sinkronisasi Odoo selesai"); load(); }
        }
      } catch { stopPoll(); setSyncing(null); }
    }, 2000);
  }, [load]);

  useEffect(() => {
    load();
    api.get("/odoo/sync/job").then((r) => { const j = r.data.data; if (j?.status === "running") { setJob(j); startPoll(); } }).catch(() => {});
    return stopPoll;
  }, [load, startPoll]);

  const sync = async (entity) => {
    setSyncing(entity);
    try {
      const r = await api.post("/odoo/sync", { entity });
      if (r.data.meta?.background) { toast.message("Sinkronisasi berjalan di latar belakang…"); setJob({ status: "running", done: 0, total: (r.data.data.entities || []).length, current: r.data.data.entities?.[0] }); startPoll(); }
      else { toast.success(entity === "all" ? "Semua entitas disinkronkan" : `${entity} disinkronkan`); setSyncing(null); load(); }
    } catch (e) { toast.error(apiError(e)); setSyncing(null); }
  };

  return (
    <div data-testid="odoo-page">
      <div className="section-head compact">
        <div><p className="eyebrow">INTEGRASI</p><h2>Sinkronisasi Odoo</h2><p>Koneksi aktif: {ready ? (ENV_LABEL[odooEnv] || odooEnv) : "memuat…"} · Scheduler otomatis tiap 60 menit</p></div>
        {canSync && odooLive && <button className="rj-btn rj-btn-green" onClick={() => sync("all")} disabled={running || !!syncing} data-testid="sync-all-btn">
          {running ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />} {running ? "Menyinkronkan…" : "Sinkronkan Semua"}
        </button>}
      </div>

      {hasPerm("odoo:connection") && <ConnectionPanel />}

      {running && (
        <div className="rj-progress" data-testid="sync-progress">
          <div className="rj-progress-head">
            <span><Loader2 size={14} className="spin" /> Menyinkronkan {ENTITY_LABEL[job.current] || job.current || "…"}</span>
            <b>{job.done}/{job.total}</b>
          </div>
          <div className="rj-progress-track"><div className="rj-progress-bar" style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }} /></div>
          <small>Proses berjalan di latar belakang — Anda tetap bisa berpindah menu.</small>
        </div>
      )}


      {!ready ? (
        <div className="rj-loading" data-testid="odoo-status-loading"><Loader2 className="spin" /> Memuat status koneksi Odoo…</div>
      ) : (
      <div className="connector-alert" data-testid="odoo-status-alert" style={odooLive ? { background: "#e8f3ee", borderColor: "#bfe0d0" } : {}}>
        <Database />
        {odooLive ? (
          <div><strong>Koneksi Odoo: {ENV_LABEL[odooEnv]} (terhubung)</strong>
            <span>Data master (customer, produk, pricelist, warehouse, pajak, karyawan, AR term) disinkronkan langsung dari Odoo. Sales Order & invoice dari Sales Hub didorong balik ke Odoo.</span></div>
        ) : (
          <div><strong>Koneksi Odoo: NONAKTIF</strong>
            <span>Belum ada koneksi Odoo yang aktif. Isi data koneksi di panel Koneksi Odoo lalu aktifkan Production atau Demo.</span></div>
        )}
      </div>
      )}

      <div className="master-sync master-sync-grid">
        {ENTITIES.map((e) => {
          const Icon = e.icon;
          const info = state.latest[e.key];
          return (
            <article key={e.key} data-testid={`sync-entity-${e.key}`}>
              <div>
                <Icon className="master-icon" />
                <strong>{e.label}</strong>
                <span>{!ready ? "Memuat…" : info ? `${info.records_processed} record · ${fmtDateTime(info.completed_at || info.started_at)}` : "Belum pernah sinkron"}</span>
              </div>
              {canSync && odooLive && (
                <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => sync(e.key)} disabled={running || !!syncing} data-testid={`sync-${e.key}-btn`}>
                  {syncing === e.key ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />} Sync
                </button>
              )}
            </article>
          );
        })}
      </div>

      <div className="panel flow-panel">
        <h3>Alur Sinkronisasi & Order</h3>
        <div className="flow">
          <div className="flow-item"><Database /><strong>Odoo</strong><span>Sumber master</span></div>
          <span><ArrowRight size={16} style={{ color: "#8a817d" }} /></span>
          <div className="flow-item"><RefreshCw /><strong>Sales Hub</strong><span>Upsert & cache</span></div>
          <span><ArrowRight size={16} style={{ color: "#8a817d" }} /></span>
          <div className="flow-item"><CheckCircle2 /><strong>Order</strong><span>Push balik ke Odoo</span></div>
        </div>
        <div className="mapping">
          {[["res.partner", "Customer & Alamat", "aktif"], ["product.product", "Produk Saleable", "aktif"],
            ["product.pricelist", "Pricelist & Item", "aktif"], ["stock.warehouse", "Warehouse", "aktif"],
            ["account.tax", "Pajak Sales", "aktif"], ["hr.employee", "Karyawan", "aktif"],
            ["sale.order", "Sales Order", odooLive ? "aktif" : "MOCK"]].map(([m, l, s]) => (
            <div key={m}><strong>{m}</strong><span>{l}</span><b className={s === "MOCK" ? "planned" : ""}>{s}</b></div>
          ))}
        </div>
      </div>

      <div className="panel" style={{ marginTop: 17 }}>
        <div className="panel-head"><div><h3>Riwayat Sinkronisasi</h3><p>30 sync run terakhir</p></div></div>
        {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : state.runs.length === 0 ? (
          <div className="rj-empty"><RefreshCw /><p>Belum ada riwayat sinkronisasi.</p></div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead><tr style={{ background: "#faf8f7", fontSize: 11, color: "#817974", textAlign: "left" }}>
                <th style={{ padding: 10 }}>Entitas</th><th style={{ padding: 10 }}>Status</th><th style={{ padding: 10 }}>Record</th><th style={{ padding: 10 }}>Waktu</th><th style={{ padding: 10 }}>Keterangan</th>
              </tr></thead>
              <tbody>
                {state.runs.map((r) => (
                  <tr key={r.id} style={{ borderTop: "1px solid #f0ecea" }}>
                    <td style={{ padding: 10, fontSize: 12, fontWeight: 700 }}>{r.entity}</td>
                    <td style={{ padding: 10 }}><span className="status">{r.status}</span></td>
                    <td style={{ padding: 10, fontSize: 12 }}>{r.records_processed}</td>
                    <td style={{ padding: 10, fontSize: 12, color: "#7b7470" }}>{fmtDateTime(r.completed_at || r.started_at)}</td>
                    <td style={{ padding: 10, fontSize: 11, color: "#8a817d" }}>{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
